/**
 * The shape of the Samtek Delivery Model. The model is code: versioned, reviewed, executed by
 * Trellis's runner, and rendered into the human-readable standard by `generate.ts`.
 *
 * Two rules from the design (docs/2026-09-06-vision-v2-delivery-model.md, "The spine"):
 *   - Customer specifics are configuration, never model. Anything that varies by program or by
 *     customer is a `Parameter` here; the program instance supplies the value.
 *   - Conformance is measured on evidence of the outcome, never on a skill having run. Every
 *     cadence therefore names the evidence that counts.
 */
import { z } from "zod";

export const RoleId = z.string().regex(/^[a-z][a-z0-9-]*$/);
export const ArtifactId = RoleId;
export const CadenceId = RoleId;
export const SkillId = RoleId;
export const ParameterId = RoleId;

/** A value the program supplies. The model names it and says what kind of thing it is. */
export const Parameter = z.object({
  id: ParameterId,
  description: z.string().min(1),
  kind: z.enum(["duration", "day-of-month", "weekday", "time-of-day", "count", "text", "list", "boolean"]),
  /** A default the model owner considers a sane starting point; still configuration, still overridable. */
  default: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).optional(),
});
export type Parameter = z.infer<typeof Parameter>;

export const Role = z.object({
  id: RoleId,
  name: z.string().min(1),
  purpose: z.string().min(1),
  /** Hard rule 1: one named owner per role. The model says so; the runner enforces it. */
  accountable: z.literal(true).default(true),
  /** Roles the same person may also hold on a small program, explicitly. */
  mayCombineWith: z.array(RoleId).default([]),
});
export type Role = z.infer<typeof Role>;

export const Artifact = z.object({
  id: ArtifactId,
  name: z.string().min(1),
  purpose: z.string().min(1),
  /** The skill that drafts it, if any. Nothing customer-facing runs above "draft" (hard rule 4). */
  draftedBy: SkillId.optional(),
  /** Who receives it. "customer" means a named human approves and a send event is the evidence. */
  audience: z.enum(["team", "manager", "portfolio", "customer"]),
});
export type Artifact = z.infer<typeof Artifact>;

export const Frequency = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("daily") }),
  z.object({ kind: z.literal("weekly"), weekday: ParameterId }),
  z.object({ kind: z.literal("monthly"), dayOfMonth: ParameterId }),
  z.object({ kind: z.literal("per-period-of-performance"), offset: ParameterId.optional() }),
  z.object({
    kind: z.literal("per-contract-event"),
    event: z.enum(["modification", "option-exercise", "task-order-award", "period-end", "contract-close"]),
  }),
  z.object({ kind: z.literal("per-item-clock"), leadTime: ParameterId }),
  z.object({ kind: z.literal("on-trigger"), trigger: z.string().min(1) }),
]);
export type Frequency = z.infer<typeof Frequency>;

/**
 * Evidence is what makes conformance measurable. A status report counts when a named human sent it
 * and the send event is on the record, not when the draft was generated.
 */
export const Evidence = z.enum([
  "send-event",          // artifact sent to its audience by a named human
  "commitment-closed",   // the commitment the cadence created reached closed
  "meeting-held",        // a meeting with the required attendees occurred (transcript or recap)
  "record-entry",        // an entry exists in the program's system of record
  "acknowledgement",     // the customer's ask was acknowledged within the standard
]);
export type Evidence = z.infer<typeof Evidence>;

export const Cadence = z.object({
  id: CadenceId,
  name: z.string().min(1),
  purpose: z.string().min(1),
  frequency: Frequency,
  /** The single accountable role. The runner creates the recurring commitment on this role's holder. */
  runsBy: RoleId,
  produces: z.array(ArtifactId).default([]),
  evidence: Evidence,
  /** A cadence that does not run is a finding; this is how loud. */
  missedIs: z.enum(["finding", "escalation"]).default("finding"),
  parameters: z.array(ParameterId).default([]),
  /** program (default) or unit. Unit ticks walk eligibleKinds. docs/2026-09-20-program-structure.md */
  scope: z.enum(["program", "unit"]).default("program"),
  eligibleKinds: z.array(z.enum(["workstream", "team"])).default([]),
});
export type Cadence = z.infer<typeof Cadence>;

export const Gate = z.object({
  id: RoleId,
  name: z.string().min(1),
  question: z.array(z.string().min(1)).min(1),
  appliesTo: z.array(ArtifactId).min(1),
});
export type Gate = z.infer<typeof Gate>;

export const EngagementRule = z.object({
  id: RoleId,
  rule: z.string().min(1),
  parameters: z.array(ParameterId).default([]),
  evidence: Evidence,
});
export type EngagementRule = z.infer<typeof EngagementRule>;

export const KnowledgeRule = z.object({
  id: RoleId,
  rule: z.string().min(1),
  parameters: z.array(ParameterId).default([]),
});
export type KnowledgeRule = z.infer<typeof KnowledgeRule>;

/** Agency levels from v2 "AI for routine work". Nothing customer-facing is ever above draft. */
export const AgencyLevel = z.enum(["draft", "act-with-approval", "act-and-notify"]);
export type AgencyLevel = z.infer<typeof AgencyLevel>;

export const Skill = z.object({
  id: SkillId,
  name: z.string().min(1),
  purpose: z.string().min(1),
  inputs: z.array(z.string().min(1)).min(1),
  output: z.string().min(1),
  agency: AgencyLevel,
  /** Which conformance question this skill's evidence answers, if any. */
  conformanceEffect: z.string().optional(),
  /** Whether the skill is in release 1. Kept in the model either way (v2 lists nine, ships seven). */
  release: z.enum(["1", "later"]),
});
export type Skill = z.infer<typeof Skill>;

/** So a one-person staff-aug order is not asked for a transition-out package it will never produce. */
export const ApplicabilityProfile = z.object({
  id: RoleId,
  name: z.string().min(1),
  description: z.string().min(1),
  cadences: z.array(CadenceId),
  artifacts: z.array(ArtifactId),
  skills: z.array(SkillId),
});
export type ApplicabilityProfile = z.infer<typeof ApplicabilityProfile>;

export const Metric = z.object({
  id: RoleId,
  name: z.string().min(1),
  definition: z.string().min(1),
  /** Denominator and weighting are the model owner's to define before any percentage is shown. */
  denominator: z.string().min(1),
});
export type Metric = z.infer<typeof Metric>;

export const Model = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  name: z.string().min(1),
  /** The one person with merge authority (v2 Q4). Unset until named; the runner refuses to pin an ownerless model to a live program. */
  owner: z.string().email().optional(),
  parameters: z.array(Parameter),
  roles: z.array(Role).min(1),
  artifacts: z.array(Artifact),
  cadences: z.array(Cadence),
  gates: z.array(Gate),
  engagement: z.array(EngagementRule),
  knowledge: z.array(KnowledgeRule),
  skills: z.array(Skill),
  profiles: z.array(ApplicabilityProfile).min(1),
  metrics: z.array(Metric),
});
export type Model = z.infer<typeof Model>;
/** What authors write: defaults may be omitted. `assertValidModel` turns it into a `Model`. */
export type ModelInput = z.input<typeof Model>;

/**
 * A program is an instance of the model: the contract, the people, the linked knowledge bases and
 * systems configure it. This is where every customer noun lives.
 */
export const ProgramInstance = z.object({
  programId: z.string().min(1),
  modelVersion: Model.shape.version,
  profile: RoleId,
  /** Role holders: exactly one person per role. One person may hold several, explicitly. */
  roles: z.record(RoleId, z.string().email()),
  parameters: z.record(ParameterId, z.union([z.string(), z.number(), z.boolean(), z.array(z.string())])),
  /** Documented exceptions: a rule the program deviates from, who approved it, and when the exception expires. */
  exceptions: z
    .array(
      z.object({
        rule: z.string().min(1),
        reason: z.string().min(1),
        approvedBy: z.string().email(),
        expires: z.string().date(),
      }),
    )
    .default([]),
});
export type ProgramInstance = z.infer<typeof ProgramInstance>;
